import { createContext, useContext, useState, useCallback, useEffect } from "react";

/*
 * The Spotters — concierge / personalization store.
 *
 * A lightweight, anonymous shopper preference profile lives in localStorage so
 * it survives across visits without an account. When a user later signs in, the
 * profile can be attached to their account (see attachToAccount hook below).
 *
 * Nothing here calls a new backend endpoint — recommendations are produced by
 * scoring the existing /products catalog client-side (see Concierge.jsx), so the
 * feature ships as a pure frontend layer.
 */

const SEEN_KEY = "ts_concierge_seen";
const PROFILE_KEY = "ts_shopper_profile";

const EMPTY = { category: null, use: null, priorities: [], price: null, size: null, completed: false };

function loadProfile() {
  try {
    const raw = localStorage.getItem(PROFILE_KEY);
    if (!raw) return { ...EMPTY };
    return { ...EMPTY, ...JSON.parse(raw) };
  } catch {
    return { ...EMPTY };
  }
}

const ConciergeCtx = createContext(null);

export function ConciergeProvider({ children }) {
  const [profile, setProfileState] = useState(loadProfile);
  const [open, setOpen] = useState(false);
  // view: "welcome" | "flow" | "results" | "profile"
  const [view, setView] = useState("welcome");
  const [seen, setSeenState] = useState(() => {
    try { return localStorage.getItem(SEEN_KEY) === "1"; } catch { return true; }
  });

  const persist = useCallback((p) => {
    try { localStorage.setItem(PROFILE_KEY, JSON.stringify(p)); } catch { /* ignore */ }
  }, []);

  const setProfile = useCallback((updater) => {
    setProfileState((prev) => {
      const next = typeof updater === "function" ? updater(prev) : { ...prev, ...updater };
      persist(next);
      return next;
    });
  }, [persist]);

  const markSeen = useCallback(() => {
    setSeenState(true);
    try { localStorage.setItem(SEEN_KEY, "1"); } catch { /* ignore */ }
  }, []);

  const openConcierge = useCallback((v) => {
    setView(v || (profile.completed ? "profile" : "welcome"));
    setOpen(true);
  }, [profile.completed]);

  const close = useCallback(() => {
    setOpen(false);
    markSeen();
  }, [markSeen]);

  const resetProfile = useCallback(() => {
    setProfileState({ ...EMPTY });
    try { localStorage.removeItem(PROFILE_KEY); } catch { /* ignore */ }
  }, []);

  const value = {
    profile, setProfile, resetProfile,
    open, view, setView, openConcierge, close,
    seen, markSeen,
    hasProfile: !!profile.completed,
  };
  return <ConciergeCtx.Provider value={value}>{children}</ConciergeCtx.Provider>;
}

export function useConcierge() {
  const ctx = useContext(ConciergeCtx);
  if (!ctx) throw new Error("useConcierge must be used within ConciergeProvider");
  return ctx;
}

// Placeholder for later: attach the anonymous profile to a logged-in account.
// Kept as a no-op export so callers/routes can wire it without a backend change.
export function useAttachProfileOnLogin() {
  useEffect(() => { /* future: POST profile to /account/preferences */ }, []);
}
